import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import './Login.scss';
import login from '../../../assets/image/login_illustration.svg';
import InputField from '../../../component/form/FormInput';
import Button from '../../../component/ui/Button';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { user_login, user_send_otp } from '../../../store/action/auth';
import { encodeEmailToToken, formatErrors } from '../../../utils/controller';
import ModalRight from '../../../component/modalRight';
import logo from '../../../assets/image/onboarding-logo.png';

const Login = ({ branding }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	// 🔹 Keep logic clean but CSS-safe
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [loader, setLoader] = useState(false);
	const [error, setError] = useState({});
	const [openPassPolicy, setOpenPassPolicy] = useState(false);

	useEffect(() => {
		document.title = 'Login | Pharmnex';
		sessionStorage.clear();
	}, []);

	const storeAuthToken = (token) => {
		localStorage.setItem('authToken', token);

		Cookies.set('authToken', token, {
			expires: 0.5, // 12 hours
			secure: true,
			sameSite: 'Strict',
		});
	};

	const handleSubmit = async (e) => {
		try {
			setLoader(true);
			setError({});

			const res = await dispatch(
				user_login({
					email,
					password,
				}),
			);

			if (!res?.success) {
				toast.error(res?.message || 'Invalid Credentials');
				setLoader(false);
				setError(formatErrors(res?.message));
				return;
			}

			const userData = res.data;
			storeAuthToken(res.token);
			window.dispatchEvent(new Event('auth-change'));

			// 🔹 Email verification flow
			if (!userData?.is_email_verified) {
				const token = encodeEmailToToken(userData.email);
				const otpRes = await dispatch(user_send_otp({ email: userData.email }));

				if (otpRes?.success) {
					toast.success(otpRes.message);
					navigate(`/otpVerify/?token=${token}&page_login=true`);
				} else {
					toast.error(otpRes?.message);
					setLoader(false);
				}
				return;
			}
			setLoader(false);

			// 🔹 Organisation flow
			const organisation = userData?.organisation;

			if (!organisation?.is_organisation_setup) {
				navigate('/organizationDetail');
				return;
			}

			// 🔹 Dashboard
			const subDomain = organisation.sub_domain;
			localStorage.setItem('suburl', subDomain);
			navigate(`/user/${subDomain}/home`);
		} catch (error) {
			setLoader(false);
		} finally {
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			handleSubmit(e);
		}
	};

	return (
		<div>
			<div className="login-form">
				<div className="login-body row clearfix">
					<div className="left-section">
						<div className="illustration">
							<div className="brand-name">
								<img
									src={logo}
									alt=""
								/>
							</div>
							<div className="login-illustration">
								<img
									src={login}
									alt=""
								/>
							</div>
						</div>
					</div>

					<div className="right-section">
						<div className="form-body py-4 ms-0">
							{branding && branding.organisation_logo && (
								<div className="brand-name">
									<img
										src={branding && branding.organisation_logo}
										alt=""
										style={{
											width: '70px',
											height: '70px',
											objectFit: 'contain',
											marginBottom: '1rem',
											borderRadius: 10,
										}}
									/>
								</div>
							)}

							<h1 className="form-title">Login</h1>

							<InputField
								value={email}
								label="Email"
								placeholder="Registered Email Address"
								type="email"
								onChange={(value) => setEmail(value)}
								error={error?.email}
								required
								name="email"
								autoFocus
								autoComplete="email"
								onKeyDown={handleKeyDown}
							/>

							<InputField
								value={password}
								label="Password"
								placeholder="Password"
								type="password"
								onChange={(value) => setPassword(value)}
								error={error?.password}
								required
								name="password"
								autoComplete="current-password"
								onKeyDown={handleKeyDown}
								showPasswordIcon
								onClick={() => setOpenPassPolicy(true)}
							/>

							<label className="form-forgotPass">
								<Link to="/forgotPassword">Forgot Password?</Link>
							</label>
						</div>

						<Button
							className="btn-primary"
							id="signin-btn"
							button="Sign in"
							loader={loader}
							onClick={handleSubmit}
						/>

						<p className="mt-lg-2 mt-3 mt-sm-3 mb-3 text-center signin-btn">
							Don’t have a PharmNex account?
							<span className="primary-color cursor-pointer font-bold">
								<Link to="/signup"> Sign up </Link>
							</span>
							now
						</p>
					</div>
				</div>
			</div>

			{openPassPolicy && (
				<ModalRight
					show
					isOpen
					onClose={() => setOpenPassPolicy(false)}
					title="Password Policy"
					description1="Use a strong password with letters and numbers."
					description2="Minimum 8 characters required."
					description3="Avoid using personal information."
				/>
			)}
		</div>
	);
};

export default Login;
